import { Button } from '@savvy-kit/components/button';
import { Skeleton } from '@savvy-kit/components/skeleton';
import { DELAY_GAIN } from '@savvy-kit/foundations/motion/tokens';
import { Text } from '@savvy-kit/foundations/text';
import { cn } from '@savvy-kit/lib/utils';
import Banner from '@savvy-kit/patterns/banner';
import { isNil } from 'es-toolkit';
import { RotateCw } from 'lucide-react';

import { useTrackEvent } from '@savvy/deprecated-shared/tracking/use-track-event';

import type { ClientPortalTimeRange } from '../common/constants';
import type { HomeBenchmark, HomeChange, HomeDayChange, HomeSeriesPoint } from './types';
import { CLIENT_PORTAL_HEADLINE_NUMBER_RESPONSIVE_CLASS } from '../common/constants';
import { HeadlineFigure } from '../common/headline-figure';
import { PortalCardError } from '../common/portal-card-error';
import { PortalCard, PortalCardFooter } from '../common/portal-section';
import { TimeRangeToggle } from '../common/time-range-toggle';
import { TransferFooterNote } from '../common/transfer-footer-note';
import { ValueChange } from '../common/value-change';
import {
  HOME_PERIOD_CAPTIONS,
  HOME_RANGE_ELEMENT_NAMES,
  HOME_STALE_BALANCES_SUFFIX,
  HOME_VALUE_ERROR_DESCRIPTION,
  HOME_VALUE_ERROR_TITLE,
  homeStaleBalancesPrefix,
} from './constants';
import { HomeValueChart } from './home-value-chart';
import { HomeValueTiles } from './home-value-tiles';

type HomeValueCardProps = {
  readonly valueLoading: boolean;
  readonly valueFailed: boolean;
  readonly onRetryValue: () => void;
  readonly statsLoading: boolean;
  readonly statsFailed: boolean;
  readonly onRetryStats: () => void;
  readonly timeRange: ClientPortalTimeRange;
  readonly timeRanges: readonly ClientPortalTimeRange[];
  readonly onTimeRangeChange: (timeRange: ClientPortalTimeRange) => void;
  readonly investmentsValue: number | null;
  readonly change: HomeChange;
  readonly series: readonly HomeSeriesPoint[];
  readonly dayChange: HomeDayChange;
  readonly totalReturnAmount: number | null;
  readonly totalReturnPercent: number | null;
  readonly benchmarks: readonly HomeBenchmark[];
  readonly investmentsHref: string | null;
  readonly inceptionLabel: string | null;
  readonly staleAsOfLabel: string | null;
  readonly transferring: boolean;
  readonly destinationCustodian: string | null;
  readonly celebrationKey?: string | null;
  /** Added to every beat on the card so nothing moves under the page's reveal fade. */
  readonly delayOffset?: number;
};

function footerNotes({
  statsFailed,
  inceptionLabel,
}: {
  readonly statsFailed: boolean;
  readonly inceptionLabel: string | null;
}): readonly string[] {
  if (statsFailed || isNil(inceptionLabel)) {
    return [];
  }
  return [`Returns shown since you joined · ${inceptionLabel}`];
}

function StaleBalancesNotice({ asOfLabel }: { readonly asOfLabel: string }) {
  return (
    <Banner
      variant="warning"
      className="mx-6 mt-6 w-auto"
      description={
        <span>
          <span className="font-semibold">{homeStaleBalancesPrefix(asOfLabel)}</span> {HOME_STALE_BALANCES_SUFFIX}
        </span>
      }
    />
  );
}

export function HomeValueCard({
  valueLoading,
  valueFailed,
  onRetryValue,
  statsLoading,
  statsFailed,
  onRetryStats,
  timeRange,
  timeRanges,
  onTimeRangeChange,
  investmentsValue,
  change,
  series,
  dayChange,
  totalReturnAmount,
  totalReturnPercent,
  benchmarks,
  investmentsHref,
  inceptionLabel,
  staleAsOfLabel,
  transferring,
  destinationCustodian,
  celebrationKey = null,
  delayOffset = 0,
}: HomeValueCardProps) {
  const trackInteraction = useTrackEvent('userInteraction');
  const allLoading = valueLoading || statsLoading;
  const beat = delayOffset;

  function handleTimeRangeChange(next: ClientPortalTimeRange) {
    trackInteraction({
      elementName: HOME_RANGE_ELEMENT_NAMES[next],
      htmlElement: 'button',
      interactionType: 'select',
    });
    onTimeRangeChange(next);
  }

  if (valueFailed) {
    return (
      <PortalCard className="overflow-hidden p-0">
        <PortalCardError
          title={HOME_VALUE_ERROR_TITLE}
          description={HOME_VALUE_ERROR_DESCRIPTION}
          onRetry={onRetryValue}
        />
      </PortalCard>
    );
  }

  const notes = transferring ? [] : footerNotes({ statsFailed, inceptionLabel });

  return (
    <PortalCard className="overflow-hidden p-0">
      {!isNil(staleAsOfLabel) && <StaleBalancesNotice asOfLabel={staleAsOfLabel} />}

      <div className="flex flex-col gap-2 px-6 pt-6">
        {allLoading ? (
          <>
            <Skeleton className="h-9 w-48 md:w-56" />
            <Skeleton className="h-5 w-44" />
          </>
        ) : (
          <>
            <HeadlineFigure
              value={investmentsValue}
              from={series.at(0)?.total}
              trigger={timeRange}
              celebrationKey={celebrationKey}
              delayOffset={beat}
              className={cn(CLIENT_PORTAL_HEADLINE_NUMBER_RESPONSIVE_CLASS, 'text-dark')}
            />
            <ValueChange
              amount={change.amount}
              percent={change.percent}
              caption={HOME_PERIOD_CAPTIONS[timeRange]}
              layout="stacked"
              motion={{
                kind: 'figure',
                trigger: timeRange,
                delay: DELAY_GAIN + beat,
                headlineFrom: series.at(0)?.total,
                headlineTo: investmentsValue,
              }}
            />
          </>
        )}
      </div>

      <div className="flex flex-col items-center gap-4 px-2 pb-6 pt-4">
        <div className="w-full">
          <HomeValueChart
            series={series}
            loading={allLoading}
            ariaLabel="Investments value over time"
            delayOffset={beat}
          />
        </div>
        <TimeRangeToggle
          value={timeRange}
          onChange={handleTimeRangeChange}
          ranges={timeRanges}
          label="Chart time range"
        />
      </div>

      {statsFailed ? (
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border-light px-6 py-4">
          <Text variant="body-sm" color="secondary">
            Your return figures are temporarily unavailable. The values above are up to date.
          </Text>
          <Button variant="secondary" size="sm" onClick={onRetryStats}>
            <RotateCw size={14} />
            Try again
          </Button>
        </div>
      ) : (
        <HomeValueTiles
          dayChange={dayChange}
          totalReturnAmount={totalReturnAmount}
          totalReturnPercent={totalReturnPercent}
          benchmarks={benchmarks}
          investmentsHref={investmentsHref}
          loading={allLoading}
          delayOffset={beat}
        />
      )}

      {transferring && (
        <PortalCardFooter className="bg-background-light">
          <TransferFooterNote custodian={destinationCustodian} />
        </PortalCardFooter>
      )}

      {!allLoading && notes.length > 0 && (
        <PortalCardFooter className="bg-background-light">
          {notes.map((note) => (
            <Text key={note} variant="caption" className="block text-center">
              {note}
            </Text>
          ))}
        </PortalCardFooter>
      )}
    </PortalCard>
  );
}
